import { Select, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { DataGrid } from "@mui/x-data-grid";
import MenuItem from "@mui/material/MenuItem";
import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import EmptyContent from "../../../../components/EmptyContent";
import { findActiveTerm, formatTo, getWeekDay } from "../../../../utils/dateTimeHelper";
import { useTranslate } from "../../../../hooks/useTranslate";
import axiosManager from "../../../../services/axiosManager";
import { useGetAbsenceCodes } from "../../../../hooks/useGetAbsenceCodes";
import Preloader from "../../../../components/Preloader";
import { useToast } from "../../../../context/toast";
import { useGetTerms } from "../../../../hooks/useGetTerms";

const formatTerm = (term) => `${term.name} ${formatTo(term.start, "yyyy", "yyyy-MM-dd")}: ${formatTo(term.start, "dd MMM", "yyyy-MM-dd")} - ${formatTo(term.end, "dd MMM", "yyyy-MM-dd")}`;

export const HistoricalAbsencesTable = (props) => {
    const {
        name, activeStudentId, setShowSuccessMessage,
    } = props;
    const toast = useToast();
    const { t } = useTranslate();

    const { terms, isLoading: isLoadingTerms } = useGetTerms();
    const { absenceCodesOptions, isLoading: isLoadingAbsenceCodes } = useGetAbsenceCodes();

    const [selectedTerm, setSelectedTerm] = useState(null);
    const [shouldTermBeChanged, setShouldTermBeChanged] = useState(false);
    const [tempTerm, setTempTerm] = useState(selectedTerm);
    const [selectedIds, setSelectedIds] = useState([]);
    const [bulkCode, setBulkCode] = useState("");
    const [rowCodes, setRowCodes] = useState([]);

    const { data: absences, isLoading: isLoadingAbsences, refetch } = useQuery({
        queryKey: ["absences", activeStudentId, selectedTerm?.start, selectedTerm?.end],
        queryFn: () => axiosManager.get$(`/parent/api/students/${activeStudentId}/absences?startDate=${selectedTerm?.start}&endDate=${selectedTerm?.end}`)
            .then((response) => response?.data),
        initialData: [],
        enabled: !!selectedTerm?.start && !!selectedTerm?.end,
    });
    const {
        mutateAsync: submitAbsences, reset: resetSubmitAbsences, isPending: isSubmitting,
    } = useMutation({
        mutationFn: (body) => axiosManager.patch$(`/parent/api/students/${activeStudentId}/absences`, body),
        onError: () => {
            toast.error(t("common.errorMessage"));
        },
        onSuccess: () => {
            refetch();
            setSelectedIds([]);
            setBulkCode("");
            setRowCodes([]);
            setShowSuccessMessage(true);
            setTimeout(() => {
                setShowSuccessMessage(false);
            }, 10000);
        },
    });

    const selectedTermText = useMemo(() => (selectedTerm ? formatTerm(selectedTerm) : ""), [selectedTerm]);
    const selectedCount = selectedIds.length;
    const someRowsHaveCodes = rowCodes.length > 0;

    useEffect(() => {
        if (!selectedTerm && terms.length) setSelectedTerm(findActiveTerm(terms));
    }, [terms]);
    useEffect(() => {
        if (shouldTermBeChanged && !tempTerm) setTempTerm(selectedTerm);
    }, [shouldTermBeChanged]);

    const handleRowCodeChange = (attendanceId, attendanceCodeId) => {
        setRowCodes((prev) => (prev.find((r) => r.attendanceId === attendanceId)
            ? prev.map((r) => (r.attendanceId === attendanceId ? { attendanceId, attendanceCodeId } : r))
            : [...prev, { attendanceId, attendanceCodeId }]));
    };

    const handleChangeTermDates = () => {
        setSelectedTerm(tempTerm);
        setShouldTermBeChanged(false);
    };

    const handleBulkApply = () => {
        if (!bulkCode || !selectedCount) return;
        setRowCodes(selectedIds.map((attendanceId) => ({ attendanceId, attendanceCodeId: bulkCode })));
        setSelectedIds([]);
        setBulkCode("");
    };

    const handleSelectionChange = (model, details) => {
        setSelectedIds(details?.reason === "multipleRowsSelection"
            ? (prev) => (prev.length === absences.length ? [] : (absences || []).map((r) => r.id))
            : [...model.ids]);
    };

    const handleSubmit = () => {
        if (!someRowsHaveCodes) return;
        submitAbsences?.(rowCodes);
    };
    const columns = useMemo(() => [
        {
            field: "day", headerName: t("historicalAbsences.columnDay"), flex: 1, valueGetter: (value, row) => getWeekDay(row.time),
        },
        {
            field: "date", headerName: t("historicalAbsences.columnDate"), flex: 1, valueGetter: (value, row) => formatTo(row.time, "dd-MMM-yyyy"),
        },
        {
            field: "time", headerName: t("historicalAbsences.columnTime"), flex: 1, valueGetter: (value, row) => formatTo(row.time, "HH:mm"),
        },
        { field: "status", headerName: t("historicalAbsences.columnStatus"), flex: 2 },
        {
            field: "code",
            headerName: t("historicalAbsences.columnRegistrationCode"),
            flex: 2,
            renderCell: ({ row }) => (
                <Select
                    size="small"
                    displayEmpty
                    fullWidth
                    value={rowCodes.find((r) => r.attendanceId === row.id)?.attendanceCodeId || ""}
                    onChange={(e) => handleRowCodeChange(row.id, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                >
                    <MenuItem value="" disabled>{t("historicalAbsences.selectAbsence")}</MenuItem>
                    {absenceCodesOptions.map((c) => (
                        <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                    ))}
                </Select>
            ),
        },
    ], [absenceCodesOptions, rowCodes, t]);

    return (
        <Preloader isLoading={isLoadingTerms || isLoadingAbsences || isLoadingAbsenceCodes} showInitiallyOverflow overflow>
            <div className="historical-absences">
                <div className="historical-absences__header">
                    {shouldTermBeChanged ? (
                        <div className="historical-absences__term-change">
                            <Typography variant="body1" component="span">
                                <strong>{t("historicalAbsences.showingTerm")}</strong>
                            </Typography>
                            <Select
                                size="small"
                                displayEmpty
                                value={tempTerm}
                                onChange={(e) => setTempTerm(e.target.value)}
                                className="historical-absences__bulk-bar__select"
                                renderValue={(val) => (val ? formatTerm(val) : t("historicalAbsences.makeSelection"))}
                                disabled={isSubmitting}
                            >
                                {terms.map((c) => (
                                    <MenuItem key={`${c.name}-${c.start}-${c.end}`} value={c}>
                                        {formatTerm(c)}
                                    </MenuItem>
                                ))}
                            </Select>
                            <Button
                                size="small"
                                variant="contained"
                                disabled={!tempTerm || isSubmitting}
                                onClick={handleChangeTermDates}
                                className="button-primary historical-absences__bulk-bar__button"
                                disableElevation
                            >
                                {t("historicalAbsences.viewTermDates")}
                            </Button>
                            <Button
                                className="btn-link historical-absences__term-change__cancel"
                                onClick={() => setShouldTermBeChanged(false)}
                            >
                                {t("button.cancel")}
                            </Button>
                        </div>
                    ) : (
                        <div className="historical-absences__term">
                            <Typography variant="body1" component="span">
                                <strong>{t("historicalAbsences.showingTerm")}</strong>
                                {selectedTermText}
                            </Typography>
                            <Button
                                className="btn-link"
                                onClick={() => setShouldTermBeChanged(true)}
                                disabled={isSubmitting}
                            >
                                {t("historicalAbsences.changeTermDates")}
                            </Button>
                        </div>
                    )}
                    <div className="historical-absences__bulk-bar">
                        <Typography variant="h5" component="span" className="historical-absences__bulk-bar__title text-primary">
                            {t("historicalAbsences.bulkApply")}
                        </Typography>
                        <Select
                            size="small"
                            displayEmpty
                            value={bulkCode || ""}
                            disabled={shouldTermBeChanged || isSubmitting}
                            onChange={(e) => setBulkCode(e.target.value?.value)}
                            className="historical-absences__bulk-bar__select"
                            renderValue={(val) => val?.label || t("historicalAbsences.makeSelection")}
                        >
                            <MenuItem value="" disabled>{t("historicalAbsences.makeSelection")}</MenuItem>
                            {absenceCodesOptions.map((c) => (
                                <MenuItem key={c.value} value={c}>{c.label}</MenuItem>
                            ))}
                        </Select>
                        <Button
                            size="small"
                            variant="contained"
                            disabled={shouldTermBeChanged || !bulkCode || !selectedCount || isSubmitting}
                            onClick={handleBulkApply}
                            className="button-primary historical-absences__bulk-bar__button"
                            disableElevation
                        >
                            {t("historicalAbsences.applyToSelected")}
                        </Button>
                        <Typography variant="h5" component="span" className="text-primary historical-absences__bulk-bar__count">
                            {`${selectedCount} ${t("historicalAbsences.selected")}`}
                        </Typography>
                    </div>
                </div>
                <EmptyContent
                    data={absences}
                    text={(
                        <div className="historical-absences-no-data">
                            <Typography variant="h4" component="div" className="text-primary">
                                {t("historicalAbsences.noDataTitle")}
                            </Typography>
                            <Typography variant="body1" component="div">
                                {t("historicalAbsences.noDataText", { name })}
                            </Typography>
                        </div>
                    )}
                >
                    <DataGrid
                        className="historical-absences-grid"
                        rows={absences}
                        columns={columns}
                        checkboxSelection
                        rowSelectionModel={{ ids: new Set(selectedIds), type: "include" }}
                        onRowSelectionModelChange={handleSelectionChange}
                        disableColumnMenu
                        disableRowSelectionOnClick
                        disableColumnSorting
                        hideFooter
                        getRowHeight={() => 58}
                        getRowId={(row) => row.id}
                    />
                    <div className="historical-absences__submit-box">
                        <Typography variant="body1" component="span" className="text-white">
                            {t("historicalAbsences.showingUnexplainedAbsences", { num: absences.length })}
                        </Typography>
                        <Button
                            size="small"
                            className="button-accent"
                            disabled={!someRowsHaveCodes || !absences.length}
                            onClick={handleSubmit}
                            loading={isSubmitting}
                        >
                            {t("historicalAbsences.submitAbsences")}
                        </Button>
                    </div>
                </EmptyContent>
            </div>
        </Preloader>
    );
};
